// Reports › Export usage (WOS-BUNDLE; parity report item 16, "usage" is CompanyOS's cost
// ledger): managers pick a date range (this month by default) and download the usage CSV, one
// row per cost entry (date, kind, seat, agent, run, model, tokens, amount). The file comes as a
// blob through the SDK, so the session cookie goes along, and is saved like the other exports.
import {
  Button,
  Field,
  FieldLabel,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  toast,
} from "@companyos/ui";
import { useMutation } from "@tanstack/react-query";
import { FileDownIcon } from "lucide-react";
import { type FormEvent, useId, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { saveBlob } from "../finance/finance-parts";
import { problemText } from "../settings/problem-text";

const day = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** This month so far: the first of the month to today (the route's own default). */
export function defaultRange(now = new Date()): { from: string; to: string } {
  return { from: `${day(now).slice(0, 8)}01`, to: day(now) };
}

export function UsageExportButton({ api, company }: { api: Api; company: CompanyAccess }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState(defaultRange);
  const bad = !range.from || !range.to || range.from > range.to;
  const download = useMutation({
    mutationFn: () => api.client.usage.export({ params: { co: company.id }, query: range }),
    onSuccess: (blob) => {
      saveBlob(blob, `usage-${company.slug}-${range.from}-to-${range.to}.csv`);
      setOpen(false);
      toast.add({ title: "Usage exported", description: `${range.from} to ${range.to}` });
    },
    onError: (e) =>
      toast.add({ title: problemText(e, "The usage was not exported."), type: "error" }),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!bad) download.mutate();
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger render={<Button variant="ghost" size="sm" data-slot="usage-export" />}>
        <FileDownIcon aria-hidden="true" />
        Export usage (CSV)
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72">
        <form onSubmit={submit} aria-label="Export usage" className="flex flex-col gap-3">
          <p className="text-sm font-medium">Usage as CSV</p>
          <p className="text-xs text-muted-foreground">
            One row per cost entry: date, kind, seat, agent, run, model, tokens and amount.
          </p>
          <div className="grid grid-cols-2 gap-2">
            <Field>
              <FieldLabel htmlFor={`${id}-from`}>From</FieldLabel>
              <Input
                id={`${id}-from`}
                type="date"
                value={range.from}
                max={range.to}
                onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor={`${id}-to`}>To</FieldLabel>
              <Input
                id={`${id}-to`}
                type="date"
                value={range.to}
                min={range.from}
                onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
              />
            </Field>
          </div>
          {bad ? (
            <p role="alert" className="text-xs text-danger-text">
              Pick a start on or before the end.
            </p>
          ) : null}
          <Button type="submit" size="sm" disabled={bad || download.isPending}>
            <FileDownIcon aria-hidden="true" />
            {download.isPending ? "Exporting…" : "Download CSV"}
          </Button>
        </form>
      </PopoverContent>
    </Popover>
  );
}
